import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Icon } from '../../components/Icon.tsx';
import { lettersApi, type Letter } from './api.ts';
import { dv } from '../../i18n/dv.ts';
import { formatDvDateShort } from '../../lib/dvDate.ts';

function LetterRow({ letter }: { letter: Letter }): JSX.Element {
  const qc = useQueryClient();

  const remove = useMutation({
    mutationFn: () => lettersApi.remove(letter.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['letters'] }); },
  });

  return (
    <div
      className="flex flex-wrap items-center gap-3 px-4 py-4 transition-colors hover:bg-[hsl(var(--surface-high))] sm:px-5"
    >
      <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]"><Icon name="description" size={22} /></span>
      <Link to="/letters/$id" params={{ id: letter.id }} className="min-w-0 flex-1 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium leading-tight">
          <span className="truncate">{letter.subject || dv.letters.newTitle}</span>
          {letter.status === 'FINALIZED' ? (
            <span className="pill pill-success shrink-0">{dv.letters.finalizedPill}</span>
          ) : (
            <span className="pill pill-neutral shrink-0">{dv.letters.draftPill}</span>
          )}
        </p>
        <p className="mt-1 truncate text-sm text-muted-foreground">
          {letter.letterNumber ?? dv.letters.draftPill}
          <span className="mx-1.5">·</span>
          {formatDvDateShort(letter.letterDate)}
        </p>
        {letter.addressTo?.officeName && (
          <p className="mt-0.5 truncate text-sm text-muted-foreground">{letter.addressTo.officeName}</p>
        )}
      </Link>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          disabled={remove.isPending}
          onClick={() => { if (window.confirm(dv.letters.deleteConfirm)) remove.mutate(); }}
          className="btn-icon text-destructive"
          aria-label={dv.common.delete}
          title={dv.common.delete}
        >
          <Icon name="delete" size={20} />
        </button>
      </div>
      {remove.isError && <p role="alert" className="w-full text-sm text-destructive">{dv.common.error}</p>}
    </div>
  );
}

export function LettersList(): JSX.Element {
  const { data: letters, isLoading, isError } = useQuery({ queryKey: ['letters'], queryFn: lettersApi.list });
  const list = letters ?? [];

  return (
    <div className="page space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold tracking-tight">{dv.letters.title}</h1>
        <Link to="/letters/new" className="btn-primary">
          <Icon name="add" size={20} />
          {dv.letters.write}
        </Link>
      </header>

      <section className="card-paper divide-y divide-border overflow-hidden p-0">
        {list.map((letter) => <LetterRow key={letter.id} letter={letter} />)}
        {isError && <p role="alert" className="p-8 text-center text-sm text-destructive">{dv.common.error}</p>}
        {!isError && list.length === 0 && (
          <div className="flex flex-col items-center gap-4 px-6 py-14 text-center">
            <div className="grid size-14 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
              <Icon name="description" size={28} />
            </div>
            <p className="text-sm text-muted-foreground">
              {isLoading ? dv.common.loading : dv.letters.empty}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
